/**
 * InitializeProfileWizard Component
 * First-time setup wizard for creating a teacher profile.
 */
import React, { useState } from 'react';

interface InitializeProfileWizardProps {
  onInitialize: (examples: string[]) => void;
  isLoading?: boolean;
}

const MIN_EXAMPLES_FOR_EXTRACTION = 3;
const MAX_EXAMPLES = 20;

const InitializeProfileWizard: React.FC<InitializeProfileWizardProps> = ({
  onInitialize,
  isLoading = false,
}) => {
  const [examples, setExamples] = useState<string[]>([]);
  const [currentExample, setCurrentExample] = useState('');
  const [showExamples, setShowExamples] = useState(false);

  const handleAddExample = () => {
    if (currentExample.trim() && examples.length < MAX_EXAMPLES) {
      setExamples([...examples, currentExample.trim()]);
      setCurrentExample('');
    }
  };

  const handleRemoveExample = (index: number) => {
    setExamples(examples.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddExample();
    }
  };

  const handleInitialize = () => {
    onInitialize(examples);
  };

  const canExtractStyle = examples.length >= MIN_EXAMPLES_FOR_EXTRACTION;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <h2 className="text-xl font-semibold">Set Up Your Feedback Profile</h2>
        <p className="mt-1 text-blue-100 text-sm">
          Create a profile to help the AI learn your feedback style
        </p>
      </div>

      <div className="p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Welcome to the Feedback Assistant
          </h3>
          <p className="text-gray-600 text-sm max-w-md mx-auto">
            Create your profile to get started. You can optionally provide
            feedback examples to help the AI learn your style.
          </p>
        </div>

        {/* Optional examples section */}
        <div className="mb-6">
          <button
            onClick={() => setShowExamples(!showExamples)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                showExamples ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
              }`}>
                {showExamples && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="text-left">
                <span className="text-sm font-medium text-gray-900">
                  Add feedback examples (optional)
                </span>
                <p className="text-xs text-gray-500">
                  Provide 3+ examples to auto-extract your feedback style
                </p>
              </div>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 text-gray-400 transition-transform ${showExamples ? 'rotate-180' : ''}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Examples input area */}
          {showExamples && (
            <div className="mt-4 p-4 border border-gray-200 rounded-lg">
              {/* Progress indicator */}
              {examples.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">
                      {examples.length} example{examples.length !== 1 ? 's' : ''} added
                    </span>
                    {canExtractStyle && (
                      <span className="text-green-600 flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Style extraction enabled
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Input area */}
              <div className="mb-4">
                <textarea
                  value={currentExample}
                  onChange={(e) => setCurrentExample(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Example: &quot;Good analysis of the primary sources, but consider strengthening your argument by addressing counterpoints in paragraph 3.&quot;"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                  disabled={examples.length >= MAX_EXAMPLES}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-500">
                    Press Enter to add
                  </span>
                  <button
                    onClick={handleAddExample}
                    disabled={!currentExample.trim() || examples.length >= MAX_EXAMPLES}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Example
                  </button>
                </div>
              </div>

              {/* Examples list */}
              {examples.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {examples.map((example, index) => (
                    <div
                      key={index}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200 group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-gray-700 line-clamp-2">
                          "{example}"
                        </p>
                        <button
                          onClick={() => handleRemoveExample(index)}
                          className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                          title="Remove"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Help text */}
              {examples.length > 0 && examples.length < MIN_EXAMPLES_FOR_EXTRACTION && (
                <p className="mt-3 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">
                  Add {MIN_EXAMPLES_FOR_EXTRACTION - examples.length} more example{MIN_EXAMPLES_FOR_EXTRACTION - examples.length !== 1 ? 's' : ''} to enable automatic style extraction.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Create button */}
        <button
          onClick={handleInitialize}
          disabled={isLoading}
          className="w-full px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          {isLoading && (
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {isLoading
            ? canExtractStyle
              ? 'Analyzing style...'
              : 'Creating profile...'
            : canExtractStyle
            ? 'Create Profile & Analyze Style'
            : 'Create Profile'}
        </button>

        <p className="mt-3 text-xs text-center text-gray-500">
          You can always add examples and configure your preferences later in Settings.
        </p>
      </div>
    </div>
  );
};

export default InitializeProfileWizard;
